import { useOptionalDependency } from 'ditox-react';
import { CloseCircle } from 'iconsax-react';
import { useSubscription } from 'observable-hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { EMPTY } from 'rxjs';
import { useLocalStorage } from 'usehooks-ts';
import { BrandLogo } from '@/components/common/brand-logo';
import type { ModalAction } from '@/components/common/modal/modal';
import { SERVICE_WORKER_MANAGER_TOKEN } from '@/container';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { useBrand } from '@/hooks/pwa/use-brand';
import useUserPlatform, { useUserHints } from '@/hooks/use-user-platform';
import { simpleLogger } from '@/services/sentry';
import { useDebugMode } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { APP_VERSION, IS_DEV, LAST_UPDATE_VERSION_STORAGE_KEY } from '@/utils/constants';
import { wait } from '@/utils/fetcher';
import cn from '@/utils/merge-class-names';
import PwaGuideModal from './pwa-guide-modal';

interface Props {
  className?: string;
}

export default function PwaPrompt({ className }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const modalRef = useRef<ModalAction>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [lastUpdateVersion, setLastUpdateVersion] = useLocalStorage(LAST_UPDATE_VERSION_STORAGE_KEY, '');
  const [debugMode] = useDebugMode();

  const brand = useBrand();
  const { isEmbedded, isIOS, isDesktop, mode } = useUserPlatform();
  const { browser } = useUserHints();
  const serviceWorkerManager = useOptionalDependency(SERVICE_WORKER_MANAGER_TOKEN);
  const { dispatch } = useCommDispatcher();

  const handleInstall = () => {
    if (isIOS) {
      modalRef.current?.open();
    } else {
      serviceWorkerManager?.triggerAppInstall();
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    serviceWorkerManager?.dismissAppInstall();
    setShowPrompt(false);
  };

  useEffect(() => {
    if (IS_DEV) return;
    if (isIOS) {
      setShowPrompt(true);
      return;
    }
  }, [isIOS]);

  useSubscription(serviceWorkerManager?.event$ ?? EMPTY, (event) => {
    switch (event.event) {
      case 'prompt-app-install':
        setShowPrompt(true);
        break;
      case 'update-available':
        if (lastUpdateVersion === event.data.version) break;
        makePrompt({
          type: 'alert',
          key: 'pwa-update',
          title: t('pwa.update.title'),
          content: event.data.version ? t('pwa.update.content', { version: event.data.version }) : undefined,
          resolveButtonLabel: t('pwa.update.confirm'),
          promise: async () => {
            setLastUpdateVersion(event.data.version ?? APP_VERSION);
            if (isEmbedded) {
              dispatch({ event: 'reload', data: { cause: 'sw-update' } });
              simpleLogger({
                name: 'RELOAD',
                op: 'comm',
                requestAttributes: {
                  cause: 'sw-update',
                },
              });
            } else {
              navigate(0);
            }
            await wait(5000);
          },
        }).catch(console.warn);
        break;
    }
  });

  if (!showPrompt || debugMode || isDesktop || mode !== 'web' || browser === 'Unknown') {
    return null;
  }

  return (
    <div
      className={cn(
        'z-60 py-2 px-5 grid grid-rows-1 grid-cols-[auto_2.5rem_1fr_6rem] h-14 items-center justify-between sticky inset-x-0 top-0 glyph-latin:gap-1 glyph-latin:xxs:gap-2 gap-2 bg-gray-100 text-metal-700 shadow-md',
        className,
      )}>
      <button
        type='button'
        className='flex size-6 items-center justify-center'
        onClick={handleDismiss}>
        <CloseCircle
          variant='Bold'
          className='size-6 text-secondary'
        />
      </button>

      <BrandLogo
        variant='full'
        className='size-10'
      />

      <div className='flex flex-col flex-1 items-stretch justify-between gap-1'>
        <div className='font-bold leading-5'>{t('pwa.download.hint-title', { brandName: brand.name })}</div>

        <div className='text-xs text-nowrap text-ellipsis glyph-latin:text-body3 glyph-latin:text-wrap'>
          {t('pwa.download.hint-content')}
        </div>
      </div>

      <button
        type='button'
        className='btn btn-primary h-10 min-h-10 min-w-20 rounded-lg p-2 text-body2 font-normal text-nowrap glyph-latin:text-body3'
        onClick={handleInstall}>
        {t('pwa.download.confirm')}
      </button>

      <PwaGuideModal ref={modalRef} />
    </div>
  );
}
